import { useId } from 'react';
import type { JSX, ReactNode } from 'react';
import { STATUS_TONES, cx } from './styles';
import type { StatusLevel } from './styles';

export interface CardProps {
  children: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  /** 头部右侧的操作区（按钮、开关…） */
  actions?: ReactNode;
  footer?: ReactNode;
  /**
   * 左侧状态条。语义等级由调用方给，颜色由组件映射 ——
   * 组件不知道"什么算异常"，也不该知道。
   */
  level?: StatusLevel;
  /** 标题的标题级别。默认 3：卡片通常嵌在页面 h1/h2 之下 */
  headingLevel?: 2 | 3 | 4;
  /** 去掉内边距：表格、地图这类自带边距的内容需要贴着卡片边 */
  padded?: boolean;
  className?: string;
}

interface CardHeadingProps {
  level: 2 | 3 | 4;
  id: string;
  children: ReactNode;
}

function CardHeading({ level, id, children }: CardHeadingProps): JSX.Element {
  const className = 'm-0 text-sm font-semibold tracking-tight text-fg';
  if (level === 2) {
    return (
      <h2 id={id} className={className}>
        {children}
      </h2>
    );
  }
  if (level === 3) {
    return (
      <h3 id={id} className={className}>
        {children}
      </h3>
    );
  }
  return (
    <h4 id={id} className={className}>
      {children}
    </h4>
  );
}

/**
 * 内容卡片：视觉上的"一件事"。高度随内容走，不参与页面布局。
 *
 * 和 `Panel` 的区别：Panel 承担**布局**（撑满高度、头部固定、正文滚动），
 * Card 只承担**分组**。需要填满一列的内容用 Panel，不要给 Card 加高度。
 */
export function Card({
  children,
  title,
  subtitle,
  actions,
  footer,
  level,
  headingLevel = 3,
  padded = true,
  className,
}: CardProps): JSX.Element {
  const headingId = `card-${useId().replaceAll(':', '')}`;
  const hasHeader = title !== undefined || subtitle !== undefined || actions !== undefined;

  return (
    <section
      {...(title === undefined ? {} : { 'aria-labelledby': headingId })}
      className={cx(
        'flex flex-col rounded-card border border-line bg-surface-1 shadow-card',
        level === undefined ? undefined : cx('border-l-2', STATUS_TONES[level].borderLeft),
        className,
      )}
    >
      {hasHeader ? (
        <header className="flex items-start gap-3 px-3.5 py-2.5">
          <div className="min-w-0 flex-1">
            {title === undefined ? null : (
              <CardHeading level={headingLevel} id={headingId}>
                {title}
              </CardHeading>
            )}
            {subtitle === undefined ? null : (
              <p className="m-0 mt-0.5 text-xs text-fg-subtle">{subtitle}</p>
            )}
          </div>
          {actions === undefined ? null : (
            <div className="flex shrink-0 items-center gap-1">{actions}</div>
          )}
        </header>
      ) : null}

      <div className={cx('min-w-0 flex-1', padded && cx('px-3.5 pb-3.5', hasHeader ? undefined : 'pt-3.5'))}>
        {children}
      </div>

      {footer === undefined ? null : (
        <footer className="border-t border-line px-3.5 py-2 text-xs text-fg-muted">{footer}</footer>
      )}
    </section>
  );
}
